import BackendAuthHandler from 'BackendAuthHandler';
import TokenData from 'TokenData';
import { AdvisorRole, UpdateAdvisorRoleResponse } from '../../models/Advisor';
import { FundUpdateEntry } from '../../models/FundUpdateEntry';
import type { ClientLogEntry } from '../clientLogCapture';
import { BaseBackendServiceInvestorClient } from './BaseBackendServiceInvestorClient';

export default class BaseBackendService extends BaseBackendServiceInvestorClient {
  consumeImpersonation(code: string): Promise<void> {
    const tag = '[impersonation:web]';
    return this.authFetchPromise(async () => {
      const consumeUrl = this.apiURL('impersonations/consume');
      console.info(tag, 'POST impersonations/consume', { url: consumeUrl });
      const response = await this.authenticatedFetch(consumeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
        credentials: 'include',
      });
      console.info(tag, 'consume response', { ok: response.ok, status: response.status });
      if (!response.ok) {
        throw this.handleError(await response.json());
      }
      try {
        await response.text();
      } catch {
        // Ignore errors reading empty response
      }
      console.info(
        tag,
        'consume OK — imp_ctx set by Set-Cookie; continuing (no userInitials probe without Bearer)',
      );
    });
  }

  getImpersonationStatus(): Promise<{
    impersonating?: boolean;
    target_sub?: string;
    actor_email?: string;
    target_email?: string;
    expiresAt?: number;
  }> {
    return this.authFetchPromise(async () => {
      const response = await this.authenticatedFetch(this.apiURL('impersonations/status'));
      if (!response.ok) {
        throw this.handleError(await response.json());
      }
      return response.json();
    });
  }

  /**
   * One batch of this tab's logs during an impersonation started with logs. Resolves false when
   * the backend refuses (capture off for the session or the deployment, or the session has
   * ended) so the caller stops capturing.
   */
  sendImpersonationClientLogs(entries: ClientLogEntry[]): Promise<boolean> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL('impersonations/client-logs'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ source: 'web', entries }),
      });
      return ![401, 403, 404].includes(response.status);
    });
  }

  exitImpersonation(): Promise<void> {
    return this.authApiMutateAuthed<void>(
      'impersonations/exit',
      'POST',
      undefined,
      () => undefined,
    );
  }

  deletePlaceholderContact(entityId: string, contactId: string): Promise<void> {
    return this.authApiDeleteVoid(
      `investment-entities/${entityId}/placeholder-contacts/${contactId}`,
    );
  }

  async getAdvisedFor(): Promise<{ externalId: string; name: string; expiresAt: number } | null> {
    return this.authFetchPromise(async (token) => {
      if (!token) {
        return null;
      }
      const response = await this.authenticatedFetch(this.apiURL('advisors/advised-for'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });
      if (response.status === 404) {
        try {
          const body = await response.json();
          if (body?.error === 'no_advisor_context') {
            return { noAdvisorContext: true } as any;
          }
        } catch {
          // treat as normal 404
        }
        return null;
      }
      if (!response.ok) {
        throw this.handleError(await response.json().catch(() => ({})));
      }
      return response.json();
    });
  }

  async exitAdvisedFor(): Promise<void> {
    return this.authFetchPromise(async (token) => {
      if (!token) {
        return;
      }
      const response = await this.authenticatedFetch(this.apiURL('adviseFor/exit'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });
      if (!response.ok) {
        throw this.handleError(await response.json());
      }
    });
  }

  async updateAdvisorAccess(
    advisorId: string,
    role?: AdvisorRole,
    entityIds?: string[],
  ): Promise<UpdateAdvisorRoleResponse> {
    const body: { role?: AdvisorRole; entityIds?: string[] } = {};
    if (role !== undefined) {
      body.role = role;
    }
    if (entityIds !== undefined) {
      body.entityIds = entityIds;
    }
    return this.authApiMutateAuthed(`advisors/${advisorId}/access`, 'PATCH', body, (json) => json);
  }

  async updateAdvisorRole(
    advisorId: string,
    role: AdvisorRole,
  ): Promise<UpdateAdvisorRoleResponse> {
    return this.updateAdvisorAccess(advisorId, role);
  }

  createReferralInvitation(request: {
    email: string;
    firstName: string;
    lastName: string;
    opportunityExternalId?: string;
  }): Promise<{
    message: string;
    invitation: {
      id: number;
      inviteeEmail: string;
      status: string;
      invitationCode: string;
      invitationLink: string;
    };
  }> {
    return this.authApiPostData(
      'referral/invite',
      request,
      'Network error while creating referral invitation',
      true,
    );
  }

  async getReferralInvitation(code: string): Promise<{
    data: {
      email: string;
      firstName: string;
      lastName: string;
      inviterName: string;
      fundOpportunity?: {
        id: string;
        name: string;
        summaryTitle: string;
        returnOnInvestment: string;
        fundSize: number;
        [key: string]: any;
      };
      contentUrl?: string;
      eoiAmount?: number;
    };
    signature?: string;
    publicKeyBase64?: string;
    globalConfig?: {
      data: import('../../models/GlobalConfiguration').GlobalConfiguration;
    };
    setupAssetStorage?: {
      token: TokenData;
    };
  }> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      const token = await new BackendAuthHandler().withAuthToken();

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await this.authenticatedFetch(this.apiURL(`referral/invitation/${code}`), {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      if (response.status === 401) {
        throw { code: 'REQUIRE_AUTHENTICATION', error: 'Authentication required' };
      }

      if (!response.ok) {
        const errorBody = await response.json();
        throw this.handleError(errorBody);
      }

      const json = await response.json();

      const redirectTo =
        typeof (json as { redirectTo?: string }).redirectTo === 'string'
          ? (json as { redirectTo: string }).redirectTo
          : null;
      if (redirectTo) {
        const url = redirectTo.startsWith('http')
          ? redirectTo
          : `${window.location.origin}${redirectTo.startsWith('/') ? '' : '/'}${redirectTo}`;
        window.location.href = url;
        return new Promise<never>(() => {});
      }

      return json;
    } catch (error: any) {
      if (error?.code && error?.error) {
        throw error;
      }
      throw this.handleError({
        code: 'NETWORK_ERROR',
        error: 'Network error while fetching referral invitation',
      });
    }
  }

  getPublishedFundUpdates(fundOpportunityId: string): Promise<FundUpdateEntry[]> {
    return this.authApiGetAuthed(
      `fundOpportunities/${fundOpportunityId}/updates/published`,
      'Network error while fetching published fund updates',
      (json) => json.data as FundUpdateEntry[],
    );
  }
}
